// URL ↔ Filters round-trip for /deals. Kept in its own file so it can be
// unit-tested without mounting DealsPage (which pulls in Supabase via
// useCatalogueDeals). The constants intentionally duplicate the ones in
// DealsPage.tsx — sub-lot 3 will move both into a shared module when the
// data layer also reads filter defaults.

import type { CatalogueTypeFilter } from './DealsPage.data';

export type Sort = 'discount' | 'priceAsc' | 'priceDesc' | 'dateDesc';

export type Filters = {
  typeFilter: CatalogueTypeFilter;
  priceMin: number;
  priceMax: number;
  areaMin: number;
  areaMax: number;
  /** Canonical DLD location keys — kept for backward-compatible external
   * `?zone_key=` / `?community_key=` / `?building_key=` links only (ADR-045 D2).
   * The UI no longer produces them: a location pick/typing sets `locationText`
   * instead (addendum ADR-045 §8, search text-first). */
  zoneKey: string[];
  communityKey: string[];
  buildingKey: string[];
  /** Free-text location term (URL `loc`) — matched ILIKE on the deal text
   * columns zone/community/building so NULL-key deals stay findable. */
  locationText: string;
  discountMin: number;
  bedrooms: string[];
  /** Property-type whitelist (ADR-027). Empty = no restriction. */
  propertyType: string[];
  q: string;
  sortBy: Sort;
};

// Sentinels « pas de filtre actif » sur ce bound (ADR-016 D11) :
//   - 0 pour les Mins : la query Postgres `gte('asking_price', 0)` matche
//     tous les deals (équivalent neutre).
//   - 999_999_999_999 (999B AED) pour priceMax : bien au-delà de toute
//     propriété réelle, fit `NUMERIC(15,2)` (~1T cap).
//   - 999_999_999 (999M sqft) pour areaMax : fit `INTEGER` (cap 2.147B).
// Quand `bound.X === sentinel` côté UI, l'input est rendu vide (placeholder
// only) — voir `DealsPage.tsx`. URL params restent omis quand au sentinel
// (clean URLs invariant).
export const PRICE_MIN = 0;
export const PRICE_MAX = 999_999_999_999;
export const AREA_MIN = 0;
export const AREA_MAX = 999_999_999;
export const DISCOUNT_MIN = 0;

export const DEFAULT_FILTERS: Filters = {
  typeFilter: 'all',
  priceMin: PRICE_MIN,
  priceMax: PRICE_MAX,
  areaMin: AREA_MIN,
  areaMax: AREA_MAX,
  zoneKey: [],
  communityKey: [],
  buildingKey: [],
  locationText: '',
  discountMin: DISCOUNT_MIN,
  bedrooms: [],
  propertyType: [],
  q: '',
  sortBy: 'discount',
};

export function readFiltersFromUrl(params: URLSearchParams): Filters {
  const numOr = (key: string, fallback: number) => {
    const raw = params.get(key);
    if (raw === null) return fallback;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const listOr = (key: string, fallback: string[]) => {
    const raw = params.get(key);
    if (!raw) return fallback;
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  };
  const sort = params.get('sort');
  const sortBy: Sort =
    sort === 'priceAsc' || sort === 'priceDesc' || sort === 'dateDesc' ? sort : 'discount';
  const type = params.get('type');
  const typeFilter: CatalogueTypeFilter = type === 'ready' || type === 'offPlan' ? type : 'all';

  // Location: canonical free-text `loc=` (search text-first, addendum ADR-045
  // §8). Legacy `?zone=Label` links (Footer / breadcrumbs / external shares)
  // are mapped to the same text term (first value) — strictly broader than the
  // old key match, so they keep working AND now surface NULL-key deals.
  // Canonical `?zone_key=` / `?community_key=` / `?building_key=` external
  // links still feed the key filters for backward compatibility.
  const locParam = params.get('loc');
  const legacyZone = params.get('zone');
  const locationText = (
    locParam !== null ? locParam : legacyZone ? (legacyZone.split(',')[0] ?? '') : ''
  ).trim();

  return {
    typeFilter,
    priceMin: numOr('priceMin', PRICE_MIN),
    priceMax: numOr('priceMax', PRICE_MAX),
    areaMin: numOr('areaMin', AREA_MIN),
    areaMax: numOr('areaMax', AREA_MAX),
    zoneKey: listOr('zone_key', []),
    communityKey: listOr('community_key', []),
    buildingKey: listOr('building_key', []),
    locationText,
    discountMin: numOr('discount_min', DISCOUNT_MIN),
    bedrooms: listOr('bedrooms', []),
    propertyType: listOr('property_type', []),
    q: params.get('q') ?? '',
    sortBy,
  };
}

/**
 * `?page=` deep linking (UI_Behavior §4.10 L675) — 1-based in the URL,
 * returned as the 0-based index the page state uses. Absent, non-integer
 * or < 2 values all normalise to the first page; an out-of-range page is
 * clamped later by DealsPage once the exact total is known.
 */
export function readPageFromUrl(params: URLSearchParams): number {
  const raw = params.get('page');
  if (raw === null) return 0;
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < 2) return 0;
  return parsed - 1;
}

export function writeFiltersToUrl(f: Filters, page1 = 1): URLSearchParams {
  const out = new URLSearchParams();
  if (f.typeFilter !== 'all') out.set('type', f.typeFilter);
  if (f.priceMin !== PRICE_MIN) out.set('priceMin', String(f.priceMin));
  if (f.priceMax !== PRICE_MAX) out.set('priceMax', String(f.priceMax));
  if (f.areaMin !== AREA_MIN) out.set('areaMin', String(f.areaMin));
  if (f.areaMax !== AREA_MAX) out.set('areaMax', String(f.areaMax));
  // Canonical free-text location (addendum ADR-045 §8). A consumed legacy
  // `?zone=` is replaced by `loc=` on the next push.
  if (f.locationText) out.set('loc', f.locationText);
  // Key params are written back only when present (inbound external links);
  // the UI never sets them anymore.
  if (f.zoneKey.length > 0) out.set('zone_key', f.zoneKey.join(','));
  if (f.communityKey.length > 0) out.set('community_key', f.communityKey.join(','));
  if (f.buildingKey.length > 0) out.set('building_key', f.buildingKey.join(','));
  if (f.discountMin !== DISCOUNT_MIN) out.set('discount_min', String(f.discountMin));
  if (f.bedrooms.length > 0) out.set('bedrooms', f.bedrooms.join(','));
  if (f.propertyType.length > 0) out.set('property_type', f.propertyType.join(','));
  if (f.q) out.set('q', f.q);
  if (f.sortBy !== 'discount') out.set('sort', f.sortBy);
  // Omitted on page 1 (clean URLs invariant, same as sentinel-valued bounds).
  if (page1 > 1) out.set('page', String(page1));
  return out;
}

export function filtersEqual(a: Filters, b: Filters): boolean {
  return (
    a.typeFilter === b.typeFilter &&
    a.priceMin === b.priceMin &&
    a.priceMax === b.priceMax &&
    a.areaMin === b.areaMin &&
    a.areaMax === b.areaMax &&
    a.discountMin === b.discountMin &&
    a.q === b.q &&
    a.locationText === b.locationText &&
    a.sortBy === b.sortBy &&
    a.zoneKey.length === b.zoneKey.length &&
    a.zoneKey.every((z, i) => z === b.zoneKey[i]) &&
    a.communityKey.length === b.communityKey.length &&
    a.communityKey.every((c, i) => c === b.communityKey[i]) &&
    a.buildingKey.length === b.buildingKey.length &&
    a.buildingKey.every((bk, i) => bk === b.buildingKey[i]) &&
    a.bedrooms.length === b.bedrooms.length &&
    a.bedrooms.every((br, i) => br === b.bedrooms[i]) &&
    a.propertyType.length === b.propertyType.length &&
    a.propertyType.every((pt, i) => pt === b.propertyType[i])
  );
}
